import React, { useEffect } from 'react';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
    useEffect(() => {
        // Scroll to the hash element if present in URL
        if (window.location.hash) {
            const element = document.querySelector(window.location.hash);
            if (element) {
                // Delay to ensure page is fully rendered
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            }
        }
    }, []);

    return (
        <div className="privacy-policy-page">
            <div className="privacy-header">
                <div className="header-content">
                    <h1>Privacy Policy</h1>
                    <p className="last-updated">Last Updated: March 21, 2026</p>
                </div>
            </div>

            <div className="privacy-content">
                <section className="privacy-section">
                    <h2>Introduction</h2>
                    <p>
                        Welcome to ACO Insights. We are committed to protecting your privacy and ensuring the security of your healthcare data. 
                        This Privacy Policy explains how we collect, use, store, and protect your information when you use our healthcare analytics platform. 
                        As a platform dealing with sensitive healthcare information, we adhere to the highest standards of data protection, including 
                        HIPAA compliance, SOC 2 Type II certification, and industry best practices for data security.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Information We Collect</h2>
                    <p>
                        ACO Insights collects several types of information to provide comprehensive healthcare analytics services. We collect CCLF 
                        (CMS Claims and Claims Line Feed) files which contain detailed claims data necessary for generating insights about emergency 
                        room visits, inpatient admissions, outpatient services, home health care, hospice care, and durable medical equipment usage. 
                        This data includes patient demographics, diagnosis codes, procedure codes, provider information, and cost data.
                    </p>
                    <p>
                        In addition to claims data, we collect user account information including your name, email address, organization name, 
                        and role within your healthcare organization. We also collect usage data such as login times, features accessed, 
                        dashboard views, report generations, and interaction patterns within the platform to improve our services and ensure 
                        optimal performance.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Authentication and Account Security</h2>
                    <p>
                        ACO Insights uses OAuth 2.0 protocol for secure authentication. When you sign in to our platform, we leverage Google OAuth 
                        to verify your identity. This means we never store your password directly. Instead, Google handles the authentication process, 
                        and we receive a secure token that confirms your identity. Your OAuth credentials, including your email address and authentication 
                        tokens, are stored securely in our database with industry-standard encryption.
                    </p>
                    <p>
                        We maintain an approved email list to control access to the platform. Only users whose email addresses have been pre-approved 
                        by your organization's administrator can access the system. This ensures that only authorized personnel within your healthcare 
                        organization can view sensitive analytics and patient data. All authentication attempts are logged for security auditing purposes.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Data Storage and Infrastructure</h2>
                    <p>
                        All data collected by ACO Insights is stored in Google BigQuery, a highly secure, enterprise-grade cloud data warehouse. 
                        BigQuery provides automatic encryption at rest using Google's default encryption, ensuring that your data is protected even 
                        at the storage level. We utilize BigQuery's column-level security and row-level access controls to ensure that users can only 
                        access data relevant to their role and organization.
                    </p>
                    <p>
                        Your CCLF files are processed and stored in dedicated BigQuery datasets with strict access controls. We implement data 
                        partitioning and clustering to optimize query performance while maintaining data integrity. All data transfers between our 
                        application and BigQuery are encrypted in transit using TLS 1.3 protocol. We maintain separate datasets for different 
                        organizations to ensure complete data isolation and prevent any cross-organization data access.
                    </p>
                    <p>
                        Our BigQuery infrastructure is configured with automatic backups, point-in-time recovery capabilities, and disaster recovery 
                        procedures to ensure your data is never lost. We retain daily snapshots of your data for 30 days and monthly snapshots for 
                        12 months to enable data recovery in case of accidental deletion or system failures.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Data Processing and Analytics</h2>
                    <p>
                        When you upload CCLF files to ACO Insights, our system processes this data to generate meaningful healthcare analytics. 
                        We parse claims data to identify trends in emergency room utilization, inpatient admissions, chronic condition management, 
                        demographic patterns, and cost analysis. All processing occurs within secure, isolated environments with no data shared 
                        between different organizations.
                    </p>
                    <p>
                        Our analytics engine runs on Google Cloud Platform infrastructure, utilizing Cloud Run for containerized microservices 
                        and FastAPI for our backend API. All API endpoints are secured with authentication tokens and rate limiting to prevent 
                        unauthorized access. We process your data in real-time to generate dashboards, but we never use your data to train machine 
                        learning models or share insights across different healthcare organizations.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>HIPAA Compliance</h2>
                    <p>
                        ACO Insights is fully compliant with the Health Insurance Portability and Accountability Act (HIPAA). We have implemented 
                        comprehensive administrative, physical, and technical safeguards to protect Protected Health Information (PHI). Our platform 
                        serves as a Business Associate to healthcare organizations, and we maintain executed Business Associate Agreements (BAAs) 
                        with all covered entities we serve.
                    </p>
                    <p>
                        We enforce strict access controls, ensuring that only authorized personnel can access PHI. All system access is logged and 
                        monitored. We conduct regular risk assessments, security audits, and vulnerability scans to identify and address potential 
                        security threats. Our team undergoes annual HIPAA training, and we maintain detailed policies and procedures for handling PHI.
                    </p>
                    <p>
                        In the event of a security incident or data breach involving PHI, we have established incident response procedures that 
                        include immediate containment, investigation, notification to affected parties as required by law, and remediation to 
                        prevent future occurrences.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Data Security Measures</h2>
                    <p>
                        We employ multiple layers of security to protect your data. All data is encrypted both in transit and at rest. We use 
                        AES-256 encryption for data at rest and TLS 1.3 for data in transit. Our infrastructure is hosted on Google Cloud Platform, 
                        which maintains SOC 2 Type II compliance and undergoes regular third-party security audits.
                    </p>
                    <p>
                        Access to production systems is restricted to essential personnel only, with all access requiring multi-factor authentication. 
                        We implement role-based access controls (RBAC) throughout our platform, ensuring users can only access data and features 
                        necessary for their role. All privileged operations are logged and monitored for suspicious activity.
                    </p>
                    <p>
                        We maintain comprehensive audit logs of all system access, data modifications, and administrative actions. These logs are 
                        stored securely and retained for a minimum of seven years for compliance purposes. Regular security assessments, penetration 
                        testing, and code reviews are conducted to identify and remediate potential vulnerabilities.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Data Sharing and Disclosure</h2>
                    <p>
                        ACO Insights does not sell, trade, rent, or otherwise transfer your data to third parties for marketing purposes. Your 
                        healthcare data remains your property, and we act solely as a data processor on your behalf. We may share your information 
                        only in the following limited circumstances: with your explicit written consent, to comply with legal obligations such as 
                        valid court orders or subpoenas, to protect the rights, property, or safety of ACO Insights, our users, or the public, 
                        or with service providers who assist in our operations under strict confidentiality and data processing agreements.
                    </p>
                    <p>
                        Any third-party service providers we engage are carefully vetted to ensure they meet our security and privacy standards. 
                        These providers are bound by contractual obligations to protect your data and use it only for the specific purposes we 
                        authorize. We maintain a current list of all subprocessors and will notify you of any changes to this list.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Your Rights and Choices</h2>
                    <p>
                        You have significant control over your data stored in ACO Insights. You have the right to access all personal information 
                        and healthcare data we maintain about you. You can request a copy of your data in a portable format at any time. If you 
                        identify any inaccuracies in your data, you have the right to request corrections, and we will update our records accordingly.
                    </p>
                    <p>
                        You may request deletion of your account and associated data, subject to legal retention requirements. We will honor such 
                        requests within 30 days unless we are required by law to retain certain information. You can also object to certain data 
                        processing activities or request restrictions on how we use your information. To exercise any of these rights, please contact 
                        our privacy team using the contact information provided below.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Data Retention</h2>
                    <p>
                        We retain your data for as long as your account remains active and as necessary to provide our services. For healthcare 
                        claims data, we follow CMS guidelines and retain data for a minimum of six years from the date of service, or longer if 
                        required by applicable state or federal regulations. User account information is retained for the duration of your 
                        organization's subscription and for an additional 90 days after termination to facilitate any data retrieval or migration needs.
                    </p>
                    <p>
                        Audit logs and security records are retained for seven years to comply with HIPAA requirements. After the retention period 
                        expires, we securely delete or anonymize your data using industry-standard data destruction methods, ensuring that it cannot 
                        be recovered or reconstructed.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Cookies and Tracking</h2>
                    <p>
                        ACO Insights uses essential cookies to maintain your session and ensure the platform functions properly. These session 
                        cookies are necessary for authentication and are automatically deleted when you close your browser. We also use analytics 
                        cookies to understand how users interact with our platform, which helps us improve user experience and identify potential issues.
                    </p>
                    <p>
                        You can control cookie settings through your browser preferences. However, disabling essential cookies may impact your 
                        ability to use certain features of our platform. We do not use third-party advertising cookies or tracking pixels.
                    </p>
                </section>

                <section className="privacy-section">
                    <h2>Updates to This Policy</h2>
                    <p>
                        We may update this Privacy Policy periodically to reflect changes in our practices, technology, legal requirements, or 
                        other factors. When we make material changes, we will notify you by email at least 30 days before the changes take effect. 
                        We will also post the updated policy on our platform with the "Last Updated" date clearly displayed. We encourage you to 
                        review this policy regularly to stay informed about how we protect your information.
                    </p>
                </section>

                <section className="contact-section" id="contact">
                    <h2>Contact Us</h2>
                    <p>
                        If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please don't hesitate 
                        to contact our privacy team. We are committed to addressing your inquiries promptly and transparently.
                    </p>
                    <div className="contact-details">
                        <div className="contact-item">
                            <strong>Email:</strong> support@sdcorner.com
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

export default PrivacyPolicy;
